import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

const quickLinks = [
  { name: 'Home', href: '#home' },
  { name: 'About Us', href: '#about' },
  { name: 'Products', href: '#products' },
  { name: 'Why Choose Us', href: '#why-us' },
  { name: 'Global Presence', href: '#global' },
  { name: 'Contact', href: '#contact' },
];

const productLinks = [
  'TVs & LED Panels',
  'Mobile Phones',
  'Laptops & Computers',
  'Gaming Consoles',
  'Printers',
  'IT Accessories',
];

const PrivacyPolicyContent = () => (
  <div className="space-y-6 text-gray-300 text-sm leading-relaxed">
    <section>
      <h3 className="text-lg font-semibold text-white mb-3">1. Introduction</h3>
      <p>
        AJKTV Overseas Private Limited ("we," "our," or "us") is committed to protecting the privacy 
        of our business partners, clients, and website visitors. This Privacy Policy explains how we 
        collect, use, disclose, and safeguard your information when you visit our website or engage 
        in business with us.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">2. Information We Collect</h3>
      <p className="mb-2">We may collect the following types of information:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li><strong>Business Contact Information:</strong> Company name, contact person name, email address, phone number, and business address</li>
        <li><strong>Inquiry Details:</strong> Product interests, order requirements, and business communications</li>
        <li><strong>Technical Data:</strong> IP address, browser type, and website usage patterns</li>
        <li><strong>Transaction Information:</strong> Purchase history, payment details, and shipping information</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">3. How We Use Your Information</h3>
      <p className="mb-2">We use the collected information to:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Process and fulfill your business inquiries and orders</li>
        <li>Communicate about products, services, and promotions</li>
        <li>Provide customer support and respond to requests</li>
        <li>Improve our website and services</li>
        <li>Comply with legal obligations and business regulations</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">4. Information Sharing</h3>
      <p>
        We do not sell, trade, or rent your personal information to third parties. We may share 
        information with trusted business partners, service providers, and logistics companies 
        solely for the purpose of fulfilling orders and providing services. We may also disclose 
        information when required by law or to protect our legal rights.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">5. Data Security</h3>
      <p>
        We implement appropriate technical and organizational security measures to protect your 
        information against unauthorized access, alteration, disclosure, or destruction. However, 
        no method of transmission over the Internet is 100% secure, and we cannot guarantee 
        absolute security.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">6. Data Retention</h3>
      <p>
        We retain your business information for as long as necessary to fulfill the purposes 
        outlined in this policy, comply with legal obligations, resolve disputes, and enforce 
        our agreements. Transaction records may be retained for accounting and tax purposes 
        as required by law.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">7. Your Rights</h3>
      <p className="mb-2">You have the right to:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Access and receive a copy of your personal data</li>
        <li>Request correction of inaccurate information</li>
        <li>Request deletion of your data (subject to legal requirements)</li>
        <li>Opt-out of marketing communications</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">8. Contact Us</h3>
      <p>
        For any privacy-related questions or requests, please contact us at:<br />
        <strong>Email:</strong> info.ajktv@gmail.com<br />
        <strong>Address:</strong> 109/2/1A, Street No. 14A, West Azad Nagar, Krishna Nagar, New Delhi -110051, India
      </p>
    </section>

    <section>
      <p className="text-gray-400 text-xs mt-6">
        Last Updated: January 2026
      </p>
    </section>
  </div>
);

const TermsOfServiceContent = () => (
  <div className="space-y-6 text-gray-300 text-sm leading-relaxed">
    <section>
      <h3 className="text-lg font-semibold text-white mb-3">1. Acceptance of Terms</h3>
      <p>
        By accessing our website or engaging in business transactions with AJKTV Overseas Private 
        Limited ("Company," "we," "our"), you agree to be bound by these Terms of Service. If you 
        do not agree to these terms, please do not use our website or services.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">2. Business Relationship</h3>
      <p>
        Our services are intended for business-to-business (B2B) transactions only. By placing 
        orders or inquiries, you represent that you are acting on behalf of a legitimate business 
        entity and have the authority to enter into binding agreements.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">3. Products and Services</h3>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>All products are sourced from authorized distributors and brand partners</li>
        <li>Product specifications, images, and descriptions are provided for reference only</li>
        <li>Prices are subject to change without prior notice</li>
        <li>Product availability is subject to stock levels and supplier conditions</li>
        <li>Minimum order quantities may apply for certain products</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">4. Orders and Pricing</h3>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>All orders are subject to acceptance and confirmation by our team</li>
        <li>Quoted prices are valid for the period specified in the quotation</li>
        <li>Prices are exclusive of applicable taxes, duties, and shipping charges unless stated otherwise</li>
        <li>We reserve the right to cancel orders due to pricing errors or stock unavailability</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">5. Payment Terms</h3>
      <p className="mb-2">Payment terms are established on a case-by-case basis and may include:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Advance payment or Letter of Credit for new customers</li>
        <li>Credit terms for established business relationships (subject to approval)</li>
        <li>All payments must be made in the agreed currency</li>
        <li>Late payments may incur interest charges as per applicable laws</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">6. Shipping and Delivery</h3>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Shipping terms (FOB, CIF, etc.) will be specified in the quotation/invoice</li>
        <li>Delivery timelines are estimates and may vary based on logistics conditions</li>
        <li>Risk of loss transfers as per agreed Incoterms</li>
        <li>Import duties and customs clearance are the buyer's responsibility unless agreed otherwise</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">7. Warranty and Returns</h3>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Products carry manufacturer's warranty as applicable</li>
        <li>Warranty claims must be processed through proper channels</li>
        <li>Returns are accepted only for defective products with prior authorization</li>
        <li>Products must be returned in original packaging and condition</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">8. Limitation of Liability</h3>
      <p>
        To the maximum extent permitted by law, AJKTV Overseas Private Limited shall not be liable 
        for any indirect, incidental, special, consequential, or punitive damages arising from 
        business transactions or use of our website. Our total liability shall not exceed the 
        value of the specific transaction in question.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">9. Intellectual Property</h3>
      <p>
        All content on this website, including logos, images, and text, is the property of 
        AJKTV Overseas Private Limited or its licensors. Brand names and logos of products 
        belong to their respective owners. Unauthorized use is prohibited.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">10. Governing Law</h3>
      <p>
        These Terms of Service shall be governed by and construed in accordance with the laws 
        of India. Any disputes arising from these terms or business transactions shall be 
        subject to the exclusive jurisdiction of the courts in New Delhi, India.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">11. Changes to Terms</h3>
      <p>
        We reserve the right to modify these Terms of Service at any time. Changes will be 
        effective upon posting on this website. Continued use of our services constitutes 
        acceptance of the modified terms.
      </p>
    </section>

    <section>
      <h3 className="text-lg font-semibold text-white mb-3">12. Contact Information</h3>
      <p>
        For questions regarding these terms, please contact us at:<br />
        <strong>Email:</strong> info.ajktv@gmail.com<br />
        <strong>Address:</strong> 109/2/1A, Street No. 14A, West Azad Nagar, Krishna Nagar, New Delhi -110051, India
      </p>
    </section>

    <section>
      <p className="text-gray-400 text-xs mt-6">
        Last Updated: January 2026
      </p>
    </section>
  </div>
);

const Footer = () => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-brand-dark border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/images/logo.jpeg" 
                alt="AJKTV Logo" 
                className="h-14 w-14 object-cover rounded-full border-2 border-brand-accent/30"
              />
              <div>
                <span className="font-outfit font-bold text-xl text-white">AJKTV</span>
                <span className="text-brand-accent text-sm block -mt-1">OVERSEAS PRIVATE LIMITED</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              Your trusted B2B partner for premium consumer electronics and IT products. 
              Connecting businesses with world-leading brands since 2018.
            </p>
            <div className="flex items-center gap-2 text-brand-accent">
              <span className="text-sm font-medium">Est. 2018</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-gray-400 hover:text-brand-accent transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-6">Products</h4>
            <ul className="space-y-3">
              {productLinks.map((product) => (
                <li key={product}>
                  <button
                    onClick={() => scrollToSection('#products')}
                    className="text-gray-400 hover:text-brand-accent transition-colors text-sm"
                  >
                    {product}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-6">Contact Us</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand-accent mt-0.5 flex-shrink-0" />
                <address className="text-gray-400 text-sm not-italic">
                  109/2/1A, Street No. 14A,<br />
                  West Azad Nagar, Krishna Nagar,<br />
                  New Delhi -110051, India
                </address>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-brand-accent flex-shrink-0" />
                <a 
                  href="mailto:info.ajktv@gmail.com" 
                  className="text-gray-400 hover:text-brand-accent transition-colors text-sm"
                >
                  info.ajktv@gmail.com
                </a>
              </div>
              <div className="pt-4">
                <div className="text-sm text-gray-500 mb-2">Global Offices</div>
                <div className="flex gap-3">
                  <img src="https://flagcdn.com/w40/in.png" alt="India" title="India" className="w-8 h-6 object-cover rounded shadow-sm" />
                  <img src="https://flagcdn.com/w40/hk.png" alt="Hong Kong" title="Hong Kong" className="w-8 h-6 object-cover rounded shadow-sm" />
                  <img src="https://flagcdn.com/w40/ae.png" alt="UAE" title="UAE" className="w-8 h-6 object-cover rounded shadow-sm" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} AJKTV Overseas Private Limited. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {/* Privacy Policy Dialog */}
            <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
              <DialogTrigger asChild>
                <button 
                  className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
                  data-testid="privacy-policy-btn"
                >
                  Privacy Policy
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl bg-brand-surface border-white/10 text-white max-h-[80vh]">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-outfit font-bold text-white">
                    Privacy Policy
                  </DialogTitle>
                </DialogHeader>
                <ScrollArea className="h-[60vh] pr-4">
                  <PrivacyPolicyContent />
                </ScrollArea>
              </DialogContent>
            </Dialog>

            {/* Terms of Service Dialog */}
            <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
              <DialogTrigger asChild>
                <button 
                  className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
                  data-testid="terms-of-service-btn"
                >
                  Terms of Service
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl bg-brand-surface border-white/10 text-white max-h-[80vh]">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-outfit font-bold text-white">
                    Terms of Service
                  </DialogTitle>
                </DialogHeader>
                <ScrollArea className="h-[60vh] pr-4">
                  <TermsOfServiceContent />
                </ScrollArea>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
